import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  breadLogicUtils_getRoutingLogicObjects,
  E2eLogicObjectsWithAasoParams,
} from 'src/e2e-redesign/business-logic/bread-logic/bread-logic-utils';
import {
  breadHandleE2eRoutingLogic,
  getFullE2ePath,
} from 'src/e2e-redesign/business-logic/bread-logic/bread-routing-logic';
import { E2eRoutingArgsI } from 'src/e2e-redesign/business-logic/e2e-routing-logic';
import useE2eStore from 'src/e2e-store';
import { ApplicationStage } from 'src/enums/aaso.enums';
import { BreadCFARoutes } from 'src/enums/routes.enums';
import { AASO, ApplicationEvents } from 'src/interfaces/aaso.interfaces';
import {
  breadReturnRouteEvent,
  breadStageTransitionMap,
  breadValidEvents,
  breadValidTransitions,
} from 'src/interfaces/bread-transition-maps';

const transitionLog = (message: string) => {
  const stackLines = new Error().stack?.split('\n').slice(2);
  const callerLine = stackLines ? stackLines[0].trim() : '';
  console.warn(`Warning: ${message} (${callerLine})`);
};

export interface AASOContextType {
  aaso: AASO | undefined;
}

export const BreadAASOContext = createContext<AASOContextType | null>(null);

export const BreadAASOProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const e2eAaso = useE2eStore((store) => store.aaso);
  const updateE2eAaso = useE2eStore((store) => store.updateAASO);

  const location = useLocation();
  const navigate = useNavigate();

  const e2eContextValue = useMemo(() => ({ aaso: e2eAaso }), [e2eAaso]);

  useEffect(() => {
    // this should update the aaso object whenever a route changes
    const path = location.pathname?.split('/').pop() as keyof typeof breadStageTransitionMap;
    const newStage = breadStageTransitionMap[path] as ApplicationStage;
    if (newStage && e2eAaso?.current_stage !== newStage) {
      //we need to check if current stage is lining up with what the latest event was, if not reset current stage and set them back to correct route.
      const currentStage = e2eAaso?.current_stage;
      if (currentStage) {
        const validEvent = breadValidEvents[currentStage as ApplicationStage];
        const validNextStages = breadValidTransitions[currentStage as ApplicationStage];
        if (validEvent && e2eAaso.latest_applicant_event?.includes(validEvent)) {
          if (validNextStages && validNextStages.includes(newStage)) {
            updateE2eAaso({ data: { current_stage: newStage }, sync: true });
          } else {
            transitionLog(`Invalid transition from ${currentStage} to ${newStage}`);
          }
        } else {
          const lastEvent = e2eAaso?.latest_applicant_event
            ? e2eAaso.latest_applicant_event[e2eAaso.latest_applicant_event.length - 1]
            : undefined;
          if (lastEvent) {
            const returnRoute = breadReturnRouteEvent[lastEvent];
            const { store_uuid, store_name, _id, e2e_form_id } = e2eAaso;
            if (_id && e2e_form_id) {
              navigate(getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, returnRoute));
            }
          }
        }
      } else {
        console.warn('Current stage is undefined');
      }
    }
  }, [location.pathname, e2eAaso?.current_stage]);

  return <BreadAASOContext.Provider value={e2eContextValue}>{children}</BreadAASOContext.Provider>;
};

export const useBreadAASO = (): AASOContextType => {
  const context = useContext(BreadAASOContext);
  if (!context) {
    throw new Error('useBreadAASO must be used within an AASOProvider');
  }
  return context;
};
